import { useEffect, useMemo, useRef } from 'react';
import BillCombo from './BillSearch';
import MetricCombo from './MetricSearch';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

import { useDataStore } from '@/store';
import { Drawer } from '@/components/Drawer';
import { DataDrawer } from '@/components/DataDrawer';
import type { BillIndexEntry, MetricIndexEntry } from '@/types';
//import DataOptions from './DataOptions'; //for optional Configuration pop, which is currently commented out

export default function Toolbar() {
  // Use individual selectors to avoid object comparison issues
  const billIndex = useDataStore((s) => s.billIndex);
  const billIndexLoaded = useDataStore((s) => s.billIndexLoaded);
  const metricIndex = useDataStore((s) => s.metricIndex);
  const metricIndexLoaded = useDataStore((s) => s.metricIndexLoaded);

  // Get functions separately - they're stable references
  const loadBillFromCongress = useDataStore((s) => s.loadBillFromCongress);

  // Data store: selection state - use individual selectors
  const congressId = useDataStore((s) => s.congressId);
  const billId = useDataStore((s) => s.billId);
  const metricKey = useDataStore((s) => s.metricKey);
  const loading = useDataStore((s) => s.loading);
  const recommendedBillId = useDataStore((s) => s.recommendedBillId);
  const recommendedMetricKeys = useDataStore((s) => s.recommendedMetricKeys);

  // Get functions separately
  const setBillId = useDataStore((s) => s.setBillId);
  const setMetricKey = useDataStore((s) => s.setMetricKey);

  // Use ref to track last loaded bill to prevent unnecessary reloads
  const lastLoadedRef = useRef<{ congressId: number; billId: string } | null>(null);
  // Keeps track of whether we've done the intial auto-select of fields (has text entry issues if it keeps doing this)
  const startAutoSelectRef = useRef(0); //using a sum here so bill and metric can both use same flag once

  // Build dropdown options from bill index (no props; Drawer builds its own too)
  const billOptions = useMemo<BillIndexEntry[]>(() => {
    if (!billIndexLoaded || !billIndex?.length || !congressId) return [];
    const cid = Number(congressId);
    if (!Number.isFinite(cid)) return [];
    return billIndex
      .filter((b) => b.congress === cid)
      .slice()
      .sort((a, b) => a.id.localeCompare(b.id));
  }, [billIndexLoaded, billIndex, congressId]);

  // Build dropdown options from metric index
  const metricOptions = useMemo<MetricIndexEntry[]>(() => {
    if (!metricIndexLoaded || !metricIndex?.length || !congressId) return [];
    const cid = Number(congressId);
    if (!Number.isFinite(cid)) return [];
    return metricIndex
      .filter((m) => m.congress === cid)
      .slice()
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [metricIndexLoaded, metricIndex, congressId]);

  // Auto-select first bill when bill index loads and no bill is selected, at most once
  useEffect(() => {
    if (startAutoSelectRef.current >= 2) return; //quit if it's already been set at start
    if (!billIndexLoaded || !billOptions.length || billId) return;

    const preferredId = 'hr1147'; // this will be the default bill, if found
    const preferredBill = billOptions.find((b) => b.id.toLowerCase() === preferredId.toLowerCase());
    const firstBill = preferredBill || billOptions[0]; //if preferred not found, default to first available bill
    if (firstBill) {
      setBillId(firstBill.id);
    }
    startAutoSelectRef.current += 1; //auto selected once, prevent doing it again (otherwise overrides user input)
  }, [billIndexLoaded, billOptions, billId, setBillId]);

  // Auto-select first metric when metric index loads and no metric is selected, at most once
  useEffect(() => {
    if (startAutoSelectRef.current >= 2) return; //Im pretty sure this flag can be shared with bills
    if (!metricIndexLoaded || !metricOptions.length || metricKey) return;

    // Auto-select the first metric
    const preferredMetricKey = 'snap_household_percent';
    const preferredMetric = metricOptions.find(
      (m) => m.id.toLowerCase() === preferredMetricKey.toLowerCase(),
    );
    const firstMetric = preferredMetric || metricOptions[0];
    if (firstMetric) {
      setMetricKey(firstMetric.id).catch((err) => {
        console.error('[Toolbar] Error auto-loading first metric:', err);
      });
    }
    startAutoSelectRef.current += 1;
  }, [metricIndexLoaded, metricOptions, metricKey, setMetricKey]);

  // Load bill data when selection changes (but not if already loading or already loaded)
  // Also trigger when index loads to ensure selected bill is loaded
  useEffect(() => {
    if (!congressId || !billId) return;
    const cidNum = Number(congressId);
    if (!Number.isFinite(cidNum)) return;

    // Check if we've already loaded this exact bill
    const lastLoaded = lastLoadedRef.current;
    if (lastLoaded?.congressId === cidNum && lastLoaded?.billId === billId) {
      // Already loaded this bill, skip
      return;
    }

    // Don't reload if already loading
    if (loading) return;

    // Load the bill and update our ref
    lastLoadedRef.current = { congressId: cidNum, billId };
    loadBillFromCongress(cidNum, billId).catch((err) => {
      // On error, reset the ref so we can retry
      if (
        lastLoadedRef.current?.congressId === cidNum &&
        lastLoadedRef.current?.billId === billId
      ) {
        lastLoadedRef.current = null;
      }
      console.error('Failed to load bill:', err);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [congressId, billId, billIndexLoaded]);
  // Note: loadBillFromCongress is a stable Zustand function and doesn't need to be in the dependency array
  // billIndexLoaded is included to ensure bill loads when index becomes available

  return (
    <>
      <Row className="g-2 mb-2 align-items-stretch">
        <Col className="d-flex">
          <Card className="w-100">
            <Card.Header className="bg-white fw-bold border-0 pb-0 h4">DATA SELECTION</Card.Header>
            <Card.Body className="py-2">
              <div className="d-flex gap-2">
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    {/* <DataOptions /> */}
                  </div>
                  {/* Congress */}
                  <Row className="g-2 align-items-center">
                    <Col xs={12}>
                      <div className="d-flex align-items-center">
                        <Form.Label
                          htmlFor="toolbar-congress"
                          className="me-2 mb-0 fw-bold"
                          style={{ minWidth: '80px' }}
                        >
                          Congress
                        </Form.Label>
                        <Form.Select
                          id="toolbar-congress"
                          size="sm"
                          value="118"
                          onChange={() => {
                            // Non-functional for now
                          }}
                          style={{ flex: '1 1 auto' }}
                        >
                          <option value="118">118</option>
                        </Form.Select>
                      </div>
                    </Col>
                  </Row>

                  {/* Bill */}
                  <Row className="g-2 align-items-center mt-2">
                    <Col xs={12}>
                      <div className="d-flex align-items-center">
                        <Form.Label
                          htmlFor="toolbar-bill"
                          className="me-2 mb-0 fw-bold"
                          style={{ minWidth: '80px' }}
                        >
                          Bill
                        </Form.Label>
                        <div
                          style={{ flex: '1 1 auto', overflow: 'visible', position: 'relative' }}
                        >
                          <BillCombo
                            items={billOptions}
                            setBillId={setBillId}
                            billId={billId}
                            recommendedBillId={recommendedBillId}
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>

                  {/* Metric */}
                  <Row className="g-2 align-items-center mt-2">
                    <Col xs={12}>
                      <div className="d-flex align-items-center">
                        <Form.Label
                          htmlFor="toolbar-metric"
                          className="me-2 mb-0 fw-bold"
                          style={{ minWidth: '80px' }}
                        >
                          Map Feature
                        </Form.Label>
                        <div
                          style={{ flex: '1 1 auto', overflow: 'visible', position: 'relative' }}
                        >
                          <MetricCombo
                            items={metricOptions}
                            setMetricKey={setMetricKey}
                            metricKey={metricKey}
                            recommendedMetricKeys={recommendedMetricKeys}
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Independent drawers that read the viz store */}
      <Drawer />
      <DataDrawer />
    </>
  );
}
